import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Settings, Save, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const CONFIG_FIELDS = [
  { key: 'points_per_link', label: 'Points per Link Protected', type: 'number', desc: 'Points awarded when a user protects an affiliate link (Tier 1).' },
  { key: 'points_per_checkout', label: 'Points per Checkout', type: 'number', desc: 'Bonus points when user reaches checkout page with protected link (Tier 2).' },
  { key: 'points_per_purchase', label: 'Points per Purchase', type: 'number', desc: 'Bonus points when user completes a purchase with protected link (Tier 3).' },
  { key: 'points_per_new_creator', label: 'Points per New Creator', type: 'number', desc: 'Bonus points when protecting a link from a creator the user hasn\'t supported before.' },
  { key: 'points_welcome_bonus', label: 'Welcome Bonus Points', type: 'number', desc: 'Points given to new users upon signup.' },
  { key: 'points_per_level', label: 'Points per Level', type: 'number', desc: 'Number of points required to advance one level.' },
  { key: 'referral_bonus_points', label: 'Referral Bonus Points', type: 'number', desc: 'Points awarded to users when someone signs up using their referral code.' },
  { key: 'point_value_cents', label: 'Point Value (cents)', type: 'number', desc: 'Cash value of each point in cents. Example: 0.25 means 1 point = $0.0025, so 1000 points = $2.50.' },
  { key: 'est_revenue_per_link', label: 'Est. Revenue per Link ($)', type: 'number', desc: 'Average estimated revenue saved per protected affiliate link (for dashboard stats).' },
  { key: 'max_daily_simulations', label: 'Max Daily Simulations', type: 'number', desc: 'Maximum number of link protection simulations per user per day.' },
  { key: 'daily_link_protect_limit', label: 'Daily Link Protect Limit', type: 'number', desc: 'Max links per user per day that earn points. Links are still protected after the limit, but no points are awarded. Prevents bot abuse.' },
  { key: 'extension_version', label: 'Extension Version', type: 'text', desc: 'Current published version of the Chrome extension.' },
];

export default function AdminConfig() {
  const { token, API } = useAuth();
  const [config, setConfig] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/config`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(res.data);
      setOriginal(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = { ...config };
      // Convert numeric fields
      CONFIG_FIELDS.forEach(f => {
        if (f.type === 'number' && payload[f.key] !== undefined) {
          payload[f.key] = parseFloat(payload[f.key]);
        }
      });
      const res = await axios.put(`${API}/admin/config`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(res.data);
      setOriginal(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert('Failed to save'); }
    setSaving(false);
  };

  const resetConfig = () => {
    setConfig({ ...original });
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(original);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!config) return <p className="text-zinc-500">Failed to load config</p>;

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-xs text-zinc-500">Configure point values, limits, and extension settings. Changes apply immediately to all users.</p>

      {/* Config Fields */}
      <div className="space-y-4">
        {CONFIG_FIELDS.map((field, i) => (
          <motion.div
            key={field.key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="glass-card rounded-xl p-5"
            data-testid={`admin-config-${field.key}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label className="text-sm font-medium text-white">{field.label}</Label>
                <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">{field.desc}</p>
              </div>
              <Input
                data-testid={`admin-config-input-${field.key}`}
                type={field.type}
                value={config[field.key] ?? ''}
                onChange={e => handleChange(field.key, e.target.value)}
                className="bg-[#09090B] border-white/10 text-white w-32 text-right font-mono"
                step={field.type === 'number' ? 'any' : undefined}
              />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 sticky bottom-0 py-4 bg-[#09090B]/80 backdrop-blur-lg">
        <button
          data-testid="admin-config-save"
          onClick={saveConfig}
          disabled={!hasChanges || saving}
          className="honey-btn px-6 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-30"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          data-testid="admin-config-reset"
          onClick={resetConfig}
          disabled={!hasChanges}
          className="px-4 py-2.5 rounded-lg text-sm bg-white/5 text-zinc-400 hover:bg-white/10 disabled:opacity-30 flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
        {saved && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-xs text-emerald-400"
          >
            Saved successfully!
          </motion.span>
        )}
      </div>
    </div>
  );
}
